import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TablePagination,
  Box,
  CircularProgress,
  Typography,
  useTheme,
  Skeleton,
  IconButton,
  Collapse,
  Menu,
  MenuItem,
  Checkbox,
  Tooltip,
  Button,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  ViewColumn as ViewColumnIcon,
  SentimentDissatisfied,
} from '@mui/icons-material';
import { visuallyHidden } from '@mui/utils';

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: any, row?: any) => string | React.ReactNode;
  sortable?: boolean;
  visible?: boolean; // optional default visibility
}

interface DataTableProps<T> {
  columns: Column[];
  data: T[];
  loading: boolean;
  page: number;
  rowsPerPage: number;
  totalRows: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSort: (property: string) => void;
  orderBy: string;
  order: 'asc' | 'desc';
  emptyMessage?: string;
  stickyHeader?: boolean;
  expandable?: boolean;
  renderExpandedRow?: (row: T) => React.ReactNode;
  onRowClick?: (row: T) => void;
  columnVisibilityKey?: string; // cookie key to persist visible columns
  columnsMenuController?: (
    open: (anchorEl: HTMLElement) => void,
    close: () => void,
    info: { getVisibleCount: () => number },
  ) => void; // optional external controller
}

export function DataTable<T>({
  columns,
  data,
  loading,
  page,
  rowsPerPage,
  totalRows,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  orderBy,
  order,
  emptyMessage = 'No data available',
  stickyHeader = true,
  expandable = false,
  renderExpandedRow,
  onRowClick,
  columnVisibilityKey,
  columnsMenuController,
}: DataTableProps<T>) {
  const theme = useTheme();
  const { palette } = theme;
  const isLight = palette.mode === 'light';

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  // Cookie helpers
  const setCookie = (name: string, value: string, days = 365) => {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/`;
  };
  const getCookie = (name: string): string | null => {
    const cname = name + '=';
    const decodedCookie = decodeURIComponent(document.cookie || '');
    const ca = decodedCookie.split(';');
    for (let c of ca) {
      while (c.charAt(0) === ' ') c = c.substring(1);
      if (c.indexOf(cname) === 0) return c.substring(cname.length, c.length);
    }
    return null;
  };

  // Visible columns state
  const defaultVisible = useMemo(() => {
    const initial: Record<string, boolean> = {};
    columns.forEach((c) => (initial[c.id] = c.visible !== false));
    if (columnVisibilityKey) {
      try {
        const saved = getCookie(`dt_vis_${columnVisibilityKey}`);
        if (saved) {
          const ids: string[] = JSON.parse(saved);
          const fromCookie: Record<string, boolean> = {};
          columns.forEach((c) => (fromCookie[c.id] = ids.includes(c.id)));
          return fromCookie;
        }
      } catch {}
    }
    return initial;
  }, [columns, columnVisibilityKey]);

  const [visibleMap, setVisibleMap] = useState<Record<string, boolean>>(defaultVisible);

  const visibleColumns = useMemo(() => columns.filter((c) => visibleMap[c.id] !== false), [columns, visibleMap]);

  const handleRequestSort = (property: string) => {
    onSort(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRowsPerPageChange(parseInt(event.target.value, 10));
    onPageChange(0);
  };

  const handleExpandRow = (index: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => setMenuAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setMenuAnchorEl(null);
  const openMenuWithAnchor = (anchorEl: HTMLElement) => setMenuAnchorEl(anchorEl);

  // Provide external controller if requested. Re-run when visibility changes so parent can update counts.
  useEffect(() => {
    if (columnsMenuController) {
      const info = { getVisibleCount: () => columns.filter((c) => visibleMap[c.id] !== false).length };
      columnsMenuController(openMenuWithAnchor, handleCloseMenu, info);
    }
  }, [columnsMenuController, columns, visibleMap]);
  const toggleColumn = (id: string) => {
    setVisibleMap((prev) => {
      const next = { ...prev, [id]: !(prev[id] !== false) ? true : false };
      if (columnVisibilityKey) {
        const visibleIds = columns.filter((c) => next[c.id] !== false).map((c) => c.id);
        setCookie(`dt_vis_${columnVisibilityKey}`, JSON.stringify(visibleIds));
      }
      return next;
    });
  };

  const renderSkeletonRows = () => {
    return Array.from({ length: rowsPerPage }).map((_, index) => (
      <TableRow key={`skeleton-${index}`} sx={{ backgroundColor: theme.palette.action.hover }}>
        {columns.map((column) => (
          <TableCell key={column.id} sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Skeleton variant="text" width="80%" />
          </TableCell>
        ))}
      </TableRow>
    ));
  };
  const headerBg = isLight ? palette.primary.lighter : palette.primary.light;
  const headerFg = isLight ? palette.text.primary : palette.common.white;

  return (
    <Paper
      sx={{
        borderRadius: 1,
        width: '100%',
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
        //  border: `1px solid ${theme.palette.divider}`,
        //  boxShadow: theme.shadows[3],
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          px: 1,
          py: 0,
          gap: 1,
        }}
      >
        <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu} keepMounted>
          {columns.map((c) => (
            <MenuItem key={c.id} onClick={() => toggleColumn(c.id)}>
              <Checkbox size="small" checked={visibleMap[c.id] !== false} />
              <Typography variant="body2">{c.label}</Typography>
            </MenuItem>
          ))}
        </Menu>
      </Box>
      <TableContainer
        sx={{
          maxHeight: 'calc(100vh - 315px)',
          // Firefox
          scrollbarWidth: 'thin',
          scrollbarColor: (theme) => `${theme.palette.primary.main} ${theme.palette.action.hover}`,
          overflowY: 'auto',

          // Chrome, Edge, Safari
          '&::-webkit-scrollbar': {
            width: '6px', // thinner vertical scrollbar (about ~50% of default)
            height: '6px', // thinner horizontal scrollbar
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: (theme) => theme.palette.action.hover,
            borderRadius: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: (theme) => theme.palette.primary.main,
            borderRadius: 8,
            border: (theme) => `2px solid ${theme.palette.action.hover}`, // adds spacing around thumb
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: (theme) => theme.palette.primary.dark,
          },
        }}
      >
        <Table stickyHeader={stickyHeader} aria-label="data table">
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  sx={{ textTransform: 'none', fontSize: 14 }}
                  style={{
                    minWidth: column.minWidth,
                    fontWeight: 700,
                    background: isLight ? palette.primary.lighter : palette.primary.light,
                    //  color: theme.palette.primary.contrastText,
                    // borderBottom: `2px solid ${theme.palette.primary.dark}`,
                    transition: 'color 0.2s',
                  }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  {column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: theme.palette.secondary.darker + ' !important',
                        },
                        '&:hover': { color: theme.palette.secondary.darker },
                      }}
                    >
                      {column.label}
                      {orderBy === column.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody
            sx={{
              '& .MuiTableCell-root': { fontSize: 13 },
              '& .MuiTypography-root': { fontSize: 13 },
            }}
          >
            {loading && data.length === 0 ? (
              renderSkeletonRows()
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 8 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    <SentimentDissatisfied fontSize="large" />
                    <Typography variant="h6">{emptyMessage}</Typography>
                    <Typography variant="body1">Try adjusting your filters or search terms</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <React.Fragment key={index}>
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    onClick={(e) => {
                      // Don't trigger row click if clicking on action buttons or their children
                      const target = e.target as HTMLElement;

                      // Prevent click if clicking on modal backdrop
                      if (
                        target.classList.contains('MuiBackdrop-root') ||
                        target.classList.contains('MuiModal-backdrop')
                      ) {
                        return;
                      }

                      // Prevent click if clicking on buttons, menus, or interactive elements
                      if (
                        target.closest('button') ||
                        target.closest('[role="menu"]') ||
                        target.closest('.MuiIconButton-root') ||
                        target.closest('.MuiMenu-root') ||
                        target.closest('.MuiDialog-root') ||
                        target.closest('.MuiDrawer-root') ||
                        target.closest('.MuiPopper-root') || // DateTimePicker, Autocomplete poppers
                        target.closest('.MuiPickersPopper-root') || // DateTimePicker specific
                        target.closest('.MuiAutocomplete-popper') || // Autocomplete specific
                        target.closest('[role="presentation"]') || // Modal/Popper presentations
                        target.closest('[data-no-row-click]') // Custom attribute for non-clickable cells
                      ) {
                        return;
                      }

                      onRowClick?.(row);
                    }}
                    sx={{
                      transition: 'background-color 0.2s, box-shadow 0.2s',
                      cursor: onRowClick ? 'pointer' : 'default',
                      '&:hover': {
                        backgroundColor: theme.palette.secondary.light,
                        //  boxShadow: '0px 3px 12px 0px rgba(100,180,255,0.1)',
                      },
                    }}
                  >
                    {visibleColumns.map((column) => {
                      const value = row[column.id];
                      const isFirstColumn = columns[0]?.id === column.id;
                      const isActionsColumn = column.id === 'actions' || column.id.toLowerCase().includes('action');
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          data-no-row-click={isActionsColumn ? 'true' : undefined}
                          sx={{
                            px: 2,
                            fontWeight: 700,
                            fontSize: 14,
                            borderBottom: expandedRows.has(index) ? 'none' : undefined,
                            ...(isFirstColumn && expandable ? { display: 'flex', alignItems: 'center', gap: 1 } : {}),
                            ...(isActionsColumn ? { cursor: 'default' } : {}),
                          }}
                        >
                          {isFirstColumn && expandable && renderExpandedRow && (
                            <IconButton
                              size="small"
                              onClick={() => handleExpandRow(index)}
                              sx={{
                                transition: 'transform 0.2s',
                                color: theme.palette.primary.main,
                                mr: 1,
                              }}
                              aria-label={expandedRows.has(index) ? 'Collapse row' : 'Expand row'}
                            >
                              {expandedRows.has(index) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          )}
                          {column.format ? column.format(value, row) : value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {expandable && renderExpandedRow && (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumns.length}
                        sx={{
                          py: 0,
                          backgroundColor: theme.palette.action.hover,
                          // borderBottom: `2px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Collapse in={expandedRows.has(index)} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1 }}>{renderExpandedRow(row)}</Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
            {loading && data.length > 0 && (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <CircularProgress size={20} color="primary" />
                    <Typography variant="body2" color="textPrimary">
                      Loading...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalRows}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          '& .MuiTablePagination-toolbar': {
            // backgroundColor: theme.palette.background.default,
            borderTop: `1px solid ${theme.palette.divider}`,
            minHeight: 30,
          },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            color: theme.palette.text.primary,
            //  fontWeight: 600,
          },
          '& .MuiTablePagination-actions button': {
            color: theme.palette.primary.main,
            '&:disabled': {
              color: theme.palette.text.disabled,
            },
          },
        }}
        labelRowsPerPage="Rows per page:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
      />
    </Paper>
  );
}
